import React, { useEffect, useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  Grid2,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  useTheme,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Receipt as ReceiptIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import CustomTypography from "../common/form/CustomTypography";
import AppButton from "../common/buttons/AppButton";
import CustomTextField from "../common/textFields/TextField";
import DatePicker2 from "../common/textFields/DatePicker2";
import ImageUpload from "../common/upload/ImageUpload";
import { PurchaseRequestProduct } from "../../../models/PurchaseRequest/PurchaseRequestProductInsights";
import {
  PurchaseOrderInvoice,
  InvoiceUploadData,
  InvoiceItemData,
  ProductSelectionState,
} from "../../../models/Invoice/PurchaseOrderInvoice";
import dayjs, { Dayjs } from "dayjs";
import ImageAPI from "../../../api/ImageAPI";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../utils/NotificationsContext";

interface InvoiceUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (uploadData: InvoiceUploadData) => Promise<void>;
  products: PurchaseRequestProduct[];
  invoices?: PurchaseOrderInvoice[];
  poId: number;
  vendorId: number;
  isUploading?: boolean;
}

const InvoiceUploadDialog: React.FC<InvoiceUploadDialogProps> = observer(
  ({
    open,
    onClose,
    onUpload,
    products,
    invoices = [],
    poId,
    vendorId,
    isUploading = false,
  }) => {
    const theme = useTheme();
    const { invoiceStore } = useStores();
    const { showError } = useNotification();

    const getRemainingQuantity = useCallback(
      (productId: number): number => {
        const product = products.find((p) => p.product_id === productId);
        if (!product) return 0;
        let totalInvoicedQuantity = 0;

        invoices.forEach((invoice) => {
          if (invoice.reject_invoice === 1) {
            return;
          }

          if (invoice.invoice_products && invoice.invoice_products.length > 0) {
            invoice.invoice_products.forEach((invoiceProduct) => {
              if (invoiceProduct.product_id === productId) {
                totalInvoicedQuantity += invoiceProduct.quantity || 0;
              }
            });
          }
        });

        const poQuantity = product.quantity || 0;
        return Math.max(0, poQuantity - totalInvoicedQuantity);
      },
      [products, invoices],
    );

    useEffect(() => {
      if (open && products.length > 0) {
        const newSelections = new Map<number, ProductSelectionState>();
        const currentSelections =
          invoiceStore.invoiceUploadState.productSelections;
        products.forEach((product) => {
          const remainingQty = getRemainingQuantity(product.product_id);
          const rate = product.rate || 0;
          const gstPercentage = product.gst_percentage || 0;
          const baseAmount = remainingQty * rate;
          const gstAmount = (baseAmount * gstPercentage) / 100;
          const totalAmount = baseAmount + gstAmount;

          const existingSelection = currentSelections.get(product.product_id);
          const shouldPreserveSelection =
            existingSelection?.selected && remainingQty > 0;

          newSelections.set(product.product_id, {
            selected: shouldPreserveSelection ? true : false,
            quantity:
              shouldPreserveSelection && existingSelection
                ? Math.min(existingSelection.quantity, remainingQty)
                : remainingQty,
            amount:
              shouldPreserveSelection && existingSelection
                ? existingSelection.amount
                : Number(totalAmount.toFixed(2)),
          });
        });
        invoiceStore.setProductSelections(newSelections);
      }
    }, [open, products, invoices, getRemainingQuantity, invoiceStore]);

    useEffect(() => {
      if (!open) {
        invoiceStore.resetInvoiceUploadState();
      }
    }, [open, invoiceStore]);

    const calculatedTotalAmount = useMemo(() => {
      let total = 0;
      invoiceStore.invoiceUploadState.productSelections.forEach((state) => {
        if (state.selected) {
          total += state.amount;
        }
      });
      if (
        invoiceStore.invoiceUploadState.otherChargesEnabled &&
        invoiceStore.invoiceUploadState.otherChargesAmount
      ) {
        total +=
          Number(invoiceStore.invoiceUploadState.otherChargesAmount) || 0;
      }
      return Number(total.toFixed(2));
    }, [
      invoiceStore.invoiceUploadState.productSelections,
      invoiceStore.invoiceUploadState.otherChargesEnabled,
      invoiceStore.invoiceUploadState.otherChargesAmount,
    ]);

    useEffect(() => {
      invoiceStore.setInvoiceUploadAmount(calculatedTotalAmount.toFixed(2));
    }, [calculatedTotalAmount, invoiceStore]);

    const handleProductToggle = (productId: number) => {
      const newMap = new Map(invoiceStore.invoiceUploadState.productSelections);
      const current = newMap.get(productId);
      if (current) {
        newMap.set(productId, {
          ...current,
          selected: !current.selected,
        });
      }
      invoiceStore.setProductSelections(newMap);
      invoiceStore.setProductsAccordionExpanded(true);
    };

    const handleQuantityChange = (productId: number, quantity: number) => {
      const product = products.find((p) => p.product_id === productId);
      if (!product) return;

      const remainingQty = getRemainingQuantity(productId);
      const validQuantity = Math.min(Math.max(0, quantity), remainingQty);

      const rate = product.rate || 0;
      const gstPercentage = product.gst_percentage || 0;
      const baseAmount = validQuantity * rate;
      const gstAmount = (baseAmount * gstPercentage) / 100;
      const totalAmount = baseAmount + gstAmount;

      const newMap = new Map(invoiceStore.invoiceUploadState.productSelections);
      const currentSelection = newMap.get(productId);
      const shouldBeSelected = currentSelection?.selected
        ? validQuantity >= 0
        : validQuantity > 0;

      newMap.set(productId, {
        selected: shouldBeSelected,
        quantity: validQuantity,
        amount: Number(totalAmount.toFixed(2)),
      });
      invoiceStore.setProductSelections(newMap);
    };

    const handleSelectAll = () => {
      const productsWithRemaining = products.filter((product) => {
        const remainingQty = getRemainingQuantity(product.product_id);
        return remainingQty > 0;
      });

      const allSelected = productsWithRemaining.every((product) => {
        const selection = invoiceStore.invoiceUploadState.productSelections.get(
          product.product_id,
        );
        return selection?.selected === true;
      });

      const newMap = new Map(invoiceStore.invoiceUploadState.productSelections);
      productsWithRemaining.forEach((product) => {
        const current = newMap.get(product.product_id);
        if (current) {
          newMap.set(product.product_id, {
            ...current,
            selected: !allSelected,
          });
        }
      });
      invoiceStore.setProductSelections(newMap);
      invoiceStore.setProductsAccordionExpanded(true);
    };

    const validateForm = (): boolean => {
      if (!invoiceStore.invoiceUploadState.invoiceFile) {
        return false;
      }
      if (!invoiceStore.invoiceUploadState.invoiceNumber.trim()) {
        return false;
      }
      if (
        !invoiceStore.invoiceUploadState.invoiceAmount ||
        Number(invoiceStore.invoiceUploadState.invoiceAmount) <= 0
      ) {
        return false;
      }
      if (!invoiceStore.invoiceUploadState.invoiceDate) {
        return false;
      }

      const selectedProducts = Array.from(
        invoiceStore.invoiceUploadState.productSelections.values(),
      ).filter((state) => state.selected);

      const hasValidOtherCharges =
        invoiceStore.invoiceUploadState.otherChargesEnabled &&
        invoiceStore.invoiceUploadState.otherChargesAmount &&
        Number(invoiceStore.invoiceUploadState.otherChargesAmount) > 0;

      if (selectedProducts.length === 0 && !hasValidOtherCharges) {
        return false;
      }

      if (selectedProducts.length > 0) {
        if (selectedProducts.some((state) => state.quantity <= 0)) {
          return false;
        }
      }

      if (
        invoiceStore.invoiceUploadState.otherChargesEnabled &&
        (!invoiceStore.invoiceUploadState.otherChargesAmount ||
          Number(invoiceStore.invoiceUploadState.otherChargesAmount) < 0)
      ) {
        return false;
      }

      if (invoiceStore.invoiceUploadState.invoiceDate) {
        const today = dayjs();
        const minAllowedDate = today.subtract(3, "month");
        if (
          invoiceStore.invoiceUploadState.invoiceDate.isBefore(minAllowedDate) ||
          invoiceStore.invoiceUploadState.invoiceDate.isAfter(today)
        ) {
          return false;
        }
      }

      return true;
    };

    const handleFileUpload = async (file: File): Promise<string> => {
      const imageAPI = new ImageAPI();
      try {
        const uploadedUrls = await imageAPI.uploadVendorDocuments(
          [file],
          "vendor-invoices",
        );
        if (uploadedUrls && uploadedUrls.length > 0) {
          return uploadedUrls[0];
        }
        throw new Error("File upload failed");
      } catch (error) {
        console.error("File upload error:", error);
        throw error;
      }
    };

    const handleUpload = async () => {
      if (!validateForm() || !invoiceStore.invoiceUploadState.invoiceFile) {
        return;
      }

      try {
        const fileUrl = await handleFileUpload(
          invoiceStore.invoiceUploadState.invoiceFile,
        );
        invoiceStore.setInvoiceFileUrl(fileUrl);

        const invoiceItems: InvoiceItemData[] = [];
        invoiceStore.invoiceUploadState.productSelections.forEach(
          (state, productId) => {
            if (state.selected && state.quantity > 0) {
              invoiceItems.push({
                product_id: productId,
                quantity: state.quantity,
                amount: state.amount,
              });
            }
          },
        );

        const uploadData: InvoiceUploadData = {
          po_id: poId,
          vendor_id: vendorId,
          invoice_url: fileUrl,
          invoice_number: invoiceStore.invoiceUploadState.invoiceNumber.trim(),
          invoice_amount: invoiceStore.invoiceUploadState.invoiceAmount.trim(),
          invoice_date: invoiceStore.invoiceUploadState.invoiceDate
            ? invoiceStore.invoiceUploadState.invoiceDate.format("YYYY-MM-DD")
            : "",
          invoice_items: invoiceItems,
          other_charges: invoiceStore.invoiceUploadState.otherChargesEnabled
            ? Number(invoiceStore.invoiceUploadState.otherChargesAmount) || 0
            : undefined,
        };

        await onUpload(uploadData);
      } catch (error: any) {
        showError(
          "Error",
          error?.response?.data?.message ||
            error?.message ||
            "Failed to upload invoice. Please try again later.",
        );
        console.error("Upload error:", error);
      }
    };

    const sortedProducts = useMemo(() => {
      return [...products].sort((a, b) => {
        const remainingA = getRemainingQuantity(a.product_id);
        const remainingB = getRemainingQuantity(b.product_id);
        if (remainingA > 0 && remainingB === 0) return -1;
        if (remainingA === 0 && remainingB > 0) return 1;
        return 0;
      });
    }, [products, getRemainingQuantity]);

    const productsWithRemaining = sortedProducts.filter((product) => {
      const remainingQty = getRemainingQuantity(product.product_id);
      return remainingQty > 0;
    });

    const allSelected =
      productsWithRemaining.length > 0 &&
      productsWithRemaining.every((product) => {
        const selection = invoiceStore.invoiceUploadState.productSelections.get(
          product.product_id,
        );
        return selection?.selected === true;
      });

    const hasSelectedProducts = Array.from(
      invoiceStore.invoiceUploadState.productSelections.values(),
    ).some((state) => state.selected);

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        slotProps={{
          transition: {
            timeout: 300,
            sx: {
              backgroundColor: theme.palette.customColors.backdrop,
              backdropFilter: "blur(4px)",
            },
          },
          backdrop: {
            timeout: 300,
            sx: {
              backgroundColor: theme.palette.customColors.backdrop,
              backdropFilter: "blur(4px)",
            },
          },
          paper: {
            sx: {
              borderRadius: "16px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
              zIndex: 2000,
            },
          },
        }}
        sx={{
          zIndex: 2000,
          "& .MuiDialog-container": {
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: `linear-gradient(90deg, ${theme.palette.customColors?.blueGradientStart} 0%, ${theme.palette.customColors?.blueGradientEnd} 50%, ${theme.palette.customColors?.blueGradientStart} 100%)`,
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
              borderBottom: `1px solid ${theme.palette.customColors?.greyBorder || theme.palette.divider}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.primary.main + "15",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ReceiptIcon sx={{ color: "primary.main", fontSize: 20 }} />
              </Box>
              <Box>
                <CustomTypography
                  variant="heading4"
                  fontWeight={600}
                  color={
                    theme.palette.customColors?.blackBorder || "text.primary"
                  }
                >
                  Upload Invoice
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  color={
                    theme.palette.customColors?.greyTextColor ||
                    "text.secondary"
                  }
                  sx={{ mt: 0.5 }}
                >
                  Fill in the invoice details below
                </CustomTypography>
              </Box>
            </Box>
            <IconButton
              onClick={onClose}
              disabled={isUploading}
              sx={{
                color:
                  theme.palette.customColors?.greyTextColor ||
                  theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor:
                    theme.palette.customColors?.lightGreyColor ||
                    theme.palette.grey[100],
                  color:
                    theme.palette.customColors?.blackBorder ||
                    theme.palette.text.primary,
                },
                "&:disabled": {
                  opacity: 0.5,
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <DialogContent
            sx={{
              padding: { xs: "20px", sm: "24px" },
              flex: 1,
              overflow: "auto",
              maxHeight: "60vh",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor:
                  theme.palette.customColors?.greyBackground ||
                  theme.palette.grey[100],
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor:
                  theme.palette.customColors?.greyDarkborder ||
                  theme.palette.grey[400],
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor:
                    theme.palette.customColors?.greyTextColor ||
                    theme.palette.grey[600],
                },
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Basic Invoice Information */}
              <Grid2 container spacing={2.5}>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <CustomTypography
                      variant="heading6"
                      style={{ fontWeight: 600, marginBottom: 16 }}
                    >
                      Invoice Number{" "}
                      <span
                        style={{
                          color: theme.palette.error.main,
                          marginLeft: 4,
                        }}
                      >
                        *
                      </span>
                    </CustomTypography>
                    <CustomTextField
                      label="Invoice Number"
                      placeholder="Enter Invoice Number"
                      value={invoiceStore.invoiceUploadState.invoiceNumber}
                      width="100%"
                      onChange={(e) =>
                        invoiceStore.setInvoiceUploadNumber(e.target.value)
                      }
                      disabled={isUploading}
                    />
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <CustomTypography
                      variant="heading6"
                      style={{ fontWeight: 600, marginBottom: 16 }}
                    >
                      Invoice Date{" "}
                      <span
                        style={{
                          color: theme.palette.error.main,
                          marginLeft: 4,
                        }}
                      >
                        *
                      </span>
                    </CustomTypography>
                    <DatePicker2
                      label="Invoice Date"
                      value={invoiceStore.invoiceUploadState.invoiceDate}
                      onChange={(date) =>
                        invoiceStore.setInvoiceUploadDate(date)
                      }
                      disabled={isUploading}
                      maxDate={dayjs()}
                      minDate={dayjs().subtract(3, "month")}
                    />
                  </Box>
                </Grid2>
              </Grid2>

              <Accordion
                expanded={
                  invoiceStore.invoiceUploadState.productsAccordionExpanded
                }
                onChange={(_, expanded) =>
                  invoiceStore.setProductsAccordionExpanded(expanded)
                }
                sx={{
                  boxShadow: "none",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "8px",
                  "&:before": {
                    display: "none",
                  },
                  "&.Mui-expanded": {
                    margin: 0,
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: 48,
                    "&.Mui-expanded": {
                      minHeight: 48,
                    },
                    "& .MuiAccordionSummary-content": {
                      margin: "12px 0",
                      "&.Mui-expanded": {
                        margin: "12px 0",
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <CustomTypography variant="heading6" fontWeight={600}>
                      Select Products{" "}
                      <span
                        style={{
                          color: theme.palette.error.main,
                          marginLeft: 4,
                        }}
                      >
                        *
                      </span>
                    </CustomTypography>
                    {productsWithRemaining.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          marginRight: 2,
                        }}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      >
                        <AppButton
                          label={allSelected ? "Clear All" : "Select All"}
                          variant="grayscale"
                          onClick={handleSelectAll}
                          disabled={isUploading}
                          customStyles={{
                            fontSize: "0.75rem",
                            padding: "4px 12px",
                            minHeight: "28px",
                            textTransform: "none",
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: "0 16px 16px 16px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      maxHeight: "60vh",
                      overflowY: "auto",
                      padding: "4px",
                      "&::-webkit-scrollbar": {
                        width: "6px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor:
                          theme.palette.customColors?.greyBackground ||
                          theme.palette.grey[100],
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor:
                          theme.palette.customColors?.greyDarkborder ||
                          theme.palette.grey[400],
                        borderRadius: "4px",
                      },
                    }}
                  >
                    {sortedProducts.map((product) => {
                      const selection =
                        invoiceStore.invoiceUploadState.productSelections.get(
                          product.product_id,
                        );
                      const remainingQty = getRemainingQuantity(
                        product.product_id,
                      );

                      if (!selection) return null;

                      const showCheckbox = remainingQty > 0;

                      return (
                        <Card
                          key={product.product_id}
                          sx={{
                            border: selection.selected
                              ? `1.5px solid ${theme.palette.primary.main}`
                              : `1px solid ${theme.palette.divider}`,
                            borderRadius: "6px",
                            backgroundColor: selection.selected
                              ? theme.palette.primary.main + "05"
                              : remainingQty === 0
                                ? theme.palette.grey[50]
                                : "transparent",
                            transition: "all 0.2s ease",
                            opacity: remainingQty === 0 ? 0.6 : 1,
                            height: "auto",
                            minHeight: "auto",
                            overflow: "visible",
                          }}
                        >
                          <CardContent
                            sx={{
                              padding: "12px 16px !important",
                              height: "auto",
                              overflow: "visible",
                              "&:last-child": {
                                paddingBottom: "12px",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1,
                                flexWrap: { xs: "wrap", sm: "nowrap" },
                                width: "100%",
                                minHeight: 0,
                              }}
                            >
                              {showCheckbox && (
                                <Box sx={{ pt: 0.5, flexShrink: 0 }}>
                                  <Checkbox
                                    checked={selection.selected}
                                    onClick={() =>
                                      handleProductToggle(product.product_id)
                                    }
                                    disabled={isUploading}
                                    size="small"
                                    sx={{ padding: 0 }}
                                  />
                                </Box>
                              )}
                              {!showCheckbox && (
                                <Box sx={{ width: 24, flexShrink: 0 }} />
                              )}

                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: { xs: "column", sm: "row" },
                                  justifyContent: "space-between",
                                  alignItems: {
                                    xs: "flex-start",
                                    sm: "flex-start",
                                  },
                                  width: "100%",
                                  flex: { xs: "1 1 auto", sm: 1 },
                                  minWidth: 0,
                                  minHeight: 0,
                                  gap: { xs: 1.5, sm: 2 },
                                }}
                              >
                                <Box
                                  sx={{
                                    flex: { xs: "1 1 auto", sm: 1 },
                                    minWidth: 0,
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    minHeight: 0,
                                  }}
                                >
                                  <CustomTypography
                                    variant="body1"
                                    fontWeight={600}
                                    style={{ marginBottom: 6, marginTop: 6 }}
                                  >
                                    {product.product_name}
                                  </CustomTypography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 1.5,
                                      alignItems: "center",
                                      marginBottom: 2,
                                    }}
                                  >
                                    <CustomTypography
                                      variant="body2"
                                      color="text.secondary"
                                      style={{ fontSize: "0.75rem" }}
                                    >
                                      <strong>Rate:</strong> ₹
                                      {product.rate || 0}
                                    </CustomTypography>
                                    <CustomTypography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ fontSize: "0.75rem" }}
                                    >
                                      <strong>PO Qty:</strong>{" "}
                                      {product.quantity || 0}
                                    </CustomTypography>
                                    <CustomTypography
                                      variant="body2"
                                      sx={{
                                        color: "error.main",
                                        fontWeight: 600,
                                      }}
                                    >
                                      <strong>Remaining:</strong> {remainingQty}
                                    </CustomTypography>
                                    <CustomTypography
                                      variant="body2"
                                      color="text.secondary"
                                      style={{ fontSize: "0.75rem" }}
                                    >
                                      <strong>GST:</strong>{" "}
                                      {product.gst_percentage || 0}%
                                    </CustomTypography>
                                    {product.hsn_code && (
                                      <CustomTypography
                                        variant="body2"
                                        color="text.secondary"
                                        style={{ fontSize: "0.75rem" }}
                                      >
                                        <strong>HSN:</strong> {product.hsn_code}
                                      </CustomTypography>
                                    )}
                                  </Box>
                                </Box>

                                {/* Quantity and Amount Fields */}
                                {selection.selected && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: {
                                        xs: "column",
                                        sm: "row",
                                      },
                                      gap: { xs: 1, sm: 1.5 },
                                      alignItems: {
                                        xs: "stretch",
                                        sm: "flex-start",
                                      },
                                      flexShrink: 0,
                                      flexGrow: 0,
                                      width: { xs: "100%", sm: "auto" },
                                      mt: { xs: 1.5, sm: 0 },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: { xs: "100%", sm: "100px" },
                                        minWidth: { xs: "100%", sm: "100px" },
                                      }}
                                    >
                                      <CustomTypography
                                        variant="body2"
                                        style={{
                                          marginBottom: 8,
                                          fontSize: "0.75rem",
                                          fontWeight: 600,
                                        }}
                                      >
                                        Quantity{" "}
                                        <span
                                          style={{
                                            color: theme.palette.error.main,
                                          }}
                                        >
                                          *
                                        </span>
                                      </CustomTypography>
                                      <CustomTextField
                                        type="number"
                                        width="100%"
                                        value={selection.quantity.toString()}
                                        onChange={(
                                          e: React.ChangeEvent<HTMLInputElement>,
                                        ) => {
                                          const inputValue = e.target.value;

                                          if (
                                            inputValue === "" ||
                                            inputValue === "-"
                                          ) {
                                            handleQuantityChange(
                                              product.product_id,
                                              0,
                                            );
                                            return;
                                          }
                                          const qty = Number(inputValue);
                                          if (isNaN(qty)) {
                                            return;
                                          }

                                          if (qty > remainingQty) {
                                            handleQuantityChange(
                                              product.product_id,
                                              remainingQty,
                                            );
                                            return;
                                          }
                                          handleQuantityChange(
                                            product.product_id,
                                            qty,
                                          );
                                        }}
                                        disabled={isUploading}
                                        allowDecimalValue={false}
                                        allowNegativeValue={false}
                                        error={
                                          selection.quantity > remainingQty ||
                                          selection.quantity <= 0
                                        }
                                      />
                                    </Box>
                                    <Box
                                      sx={{
                                        width: { xs: "100%", sm: "120px" },
                                        minWidth: { xs: "100%", sm: "120px" },
                                      }}
                                    >
                                      <CustomTypography
                                        variant="body2"
                                        style={{
                                          marginBottom: 8,
                                          fontSize: "0.75rem",
                                          fontWeight: 600,
                                        }}
                                      >
                                        Amount
                                      </CustomTypography>
                                      <CustomTextField
                                        value={`₹${selection.amount.toFixed(2)}`}
                                        disabled
                                        width="100%"
                                      />
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        invoiceStore.invoiceUploadState.otherChargesEnabled
                      }
                      onChange={(e) =>
                        invoiceStore.setOtherChargesEnabled(e.target.checked)
                      }
                      disabled={isUploading}
                    />
                  }
                  label={
                    <CustomTypography variant="heading6" fontWeight={600}>
                      Other Charges
                    </CustomTypography>
                  }
                />
                <Box>
                  {invoiceStore.invoiceUploadState.otherChargesEnabled && (
                    <Box>
                      <CustomTextField
                        label="Other Charges Amount"
                        placeholder="Enter amount"
                        value={
                          invoiceStore.invoiceUploadState.otherChargesAmount
                        }
                        onChange={(e) =>
                          invoiceStore.setOtherChargesAmount(e.target.value)
                        }
                        type="number"
                        allowDecimalValue={true}
                        allowNegativeValue={false}
                        disabled={isUploading}
                        width="100%"
                      />
                    </Box>
                  )}
                </Box>
              </Box>

              <Grid2 container spacing={2.5}>
                <Grid2 size={{ xs: 12 }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <CustomTypography
                      variant="heading6"
                      style={{ fontWeight: 600, marginBottom: 16 }}
                    >
                      Total Invoice Amount{" "}
                      <span
                        style={{
                          color: theme.palette.error.main,
                          marginLeft: 4,
                        }}
                      >
                        *
                      </span>
                    </CustomTypography>
                    <CustomTextField
                      label="Total Invoice Amount"
                      placeholder="Auto-calculated"
                      value={invoiceStore.invoiceUploadState.invoiceAmount}
                      width="100%"
                      disabled={true}
                      type="number"
                      allowDecimalValue={true}
                      allowNegativeValue={false}
                      helperText={
                        invoiceStore.invoiceUploadState.otherChargesEnabled &&
                        invoiceStore.invoiceUploadState.otherChargesAmount &&
                        Array.from(
                          invoiceStore.invoiceUploadState.productSelections.values(),
                        ).filter((s) => s.selected).length === 0
                          ? "Auto-calculated from other charges"
                          : `Auto-calculated from selected products${invoiceStore.invoiceUploadState.otherChargesEnabled && invoiceStore.invoiceUploadState.otherChargesAmount ? " and other charges" : ""}`
                      }
                    />
                  </Box>
                </Grid2>
              </Grid2>

              {/* File Upload */}
              <Box>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 2 }}
                >
                  Invoice File{" "}
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                </CustomTypography>
                <ImageUpload
                  showImagesList={true}
                  existingImageList={[]}
                  newImageList={
                    invoiceStore.invoiceUploadState.invoiceFile
                      ? [invoiceStore.invoiceUploadState.invoiceFile]
                      : []
                  }
                  addImagesToBeUploaded={(files) => {
                    if (files.length > 0) {
                      invoiceStore.setInvoiceUploadFile(files[0]);
                    }
                  }}
                  removeImage={() => {
                    invoiceStore.setInvoiceUploadFile(null);
                  }}
                  size="large"
                  multiple={false}
                  allowedFileTypes=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  disabled={isUploading}
                  minHeight="120px"
                />
              </Box>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              padding: { xs: "10px 16px 10px", sm: "16px 20px 20px" },
              borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
              backgroundColor: theme.palette.customColors.lightGreyColor,
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <AppButton
              onClick={onClose}
              variant="grayscale"
              label="Cancel"
              customStyles={{
                borderColor: theme.palette.customColors.greyDarkborder,
                color: theme.palette.customColors.greyTextColor,
                fontWeight: 600,
                padding: "8px 20px",
                borderRadius: "8px",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                },
              }}
              disabled={isUploading}
            />

            <AppButton
              onClick={handleUpload}
              variant="primary"
              label="Upload"
              disabled={isUploading || !validateForm()}
              startIcon={
                isUploading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            />
          </DialogActions>
        </Box>
      </Dialog>
    );
  },
);

export default InvoiceUploadDialog;
